import { BaseApiService, CloudProvider, CloudConnection, CloudFile, SyncTask, SyncLog } from './base';
import API_CONFIG, { API_ENDPOINTS } from '../../config/api';
import { buildApiUrl } from '../../utils/apiUrl';

export class CloudService extends BaseApiService {
  // Obtenir les fournisseurs cloud disponibles
  async getCloudProviders(): Promise<CloudProvider[]> {
    try {
      const response: any = await this.request(API_ENDPOINTS.CLOUD_PROVIDERS);
      return response.results || response || [];
    } catch (error) {
      console.error('Erreur récupération fournisseurs cloud:', error);
      throw error;
    }
  }

  // Obtenir les connexions cloud de l'utilisateur
  async getCloudConnections(): Promise<CloudConnection[]> {
    try {
      const response: any = await this.request(API_ENDPOINTS.CLOUD_CONNECTIONS);
      return response.results || response || [];
    } catch (error) {
      console.error('Erreur récupération connexions cloud:', error);
      throw error;
    }
  }

  // Obtenir l'URL d'authentification OAuth
  async getAuthUrl(providerName: string): Promise<{ auth_url: string; provider: string; state?: string }> {
    try {
      const response: any = await this.request(API_ENDPOINTS.CLOUD_OAUTH_AUTH, {
        method: 'POST',
        body: JSON.stringify({ provider: providerName })
      });
      return response;
    } catch (error) {
      console.error('Erreur récupération URL OAuth:', error);
      throw error;
    }
  }

  // Gérer le callback OAuth
  async handleOAuthCallback(code: string, state?: string, provider?: string): Promise<{ success: boolean; message: string; connection_id: number; user_info?: any }> {
    try {
      // Extraire le provider du state si nécessaire
      const providerName = provider || (state ? state.split('_')[0] : 'google-drive');
      
      const response: any = await this.request(API_ENDPOINTS.CLOUD_OAUTH_CALLBACK, {
        method: 'POST',
        body: JSON.stringify({ 
          code, 
          state, 
          provider: providerName 
        })
      });
      return response;
    } catch (error) {
      console.error('Erreur callback OAuth:', error);
      throw error;
    }
  }

  // Supprimer une connexion cloud
  async deleteCloudConnection(connectionId: number): Promise<{ success: boolean; message: string }> {
    try {
      const response: any = await this.request(`${API_ENDPOINTS.CLOUD_CONNECTIONS}${connectionId}/`, {
        method: 'DELETE'
      });
      return {
        success: true,
        message: 'Connexion cloud supprimée avec succès'
      };
    } catch (error) {
      console.error('Erreur suppression connexion cloud:', error);
      throw error;
    }
  }

  // Lancer une synchronisation
  async syncCloudFiles(connectionId: number): Promise<{ success: boolean; task_id: number; result: any }> {
    try {
      console.log(`🔄 Démarrage de la synchronisation pour la connexion ${connectionId}`);
      
      // Pas de timeout pour cette requête - la synchronisation peut prendre beaucoup de temps
      // Certains utilisateurs peuvent avoir des milliers de fichiers dans leur Google Drive
      const endpoint = API_ENDPOINTS.CLOUD_CONNECTION_SYNC(connectionId.toString());
      // BEST PRACTICE: Utiliser buildApiUrl() pour construire l'URL de manière sûre
      const url = buildApiUrl(endpoint);
      const headers = this.getHeaders();
      
      console.log(`📡 Endpoint de synchronisation: ${endpoint}`);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: headers
        // Pas de signal/AbortController pour permettre une synchronisation sans limite de temps
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let errorMessage = `HTTP error! status: ${response.status}`;
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('✅ Réponse de synchronisation reçue:', data);
      
      return {
        success: data.success !== false, // Par défaut true sauf si explicitement false
        task_id: data.task_id,
        result: data.result || data
      };
    } catch (error: any) {
      console.error('❌ Erreur synchronisation fichiers cloud:', error);
      console.error('❌ Détails:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status
      });
      throw error;
    }
  }

  // Obtenir les fichiers d'une connexion
  async getCloudFiles(connectionId: number): Promise<CloudFile[]> {
    try {
      console.log(`📡 Appel API getCloudFiles pour la connexion ${connectionId}`);
      const endpoint = API_ENDPOINTS.CLOUD_CONNECTION_FILES(connectionId.toString());
      console.log(`📡 Endpoint: ${endpoint}`);
      
      const response: any = await this.request(endpoint);
      console.log('📡 Réponse brute de l\'API:', response);
      console.log('📡 Type de réponse:', typeof response);
      console.log('📡 Est un tableau?', Array.isArray(response));
      
      // Gérer différents formats de réponse
      let files: CloudFile[] = [];
      if (Array.isArray(response)) {
        files = response;
      } else if (response && Array.isArray(response.results)) {
        files = response.results;
      } else if (response && Array.isArray(response.data)) {
        files = response.data;
      } else if (response && typeof response === 'object') {
        // Si c'est un objet mais pas un tableau, essayer de trouver les fichiers
        console.warn('⚠️ Réponse inattendue, format objet:', Object.keys(response));
        files = [];
      }
      
      console.log(`✅ ${files.length} fichier(s) extrait(s) de la réponse`);
      return files;
    } catch (error) {
      console.error('❌ Erreur récupération fichiers cloud:', error);
      throw error;
    }
  }

  // Uploader un fichier vers le cloud
  async uploadCloudFile(connectionId: number, file: File, folderId?: string): Promise<CloudFile> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('connection_id', connectionId.toString());
      if (folderId) {
        formData.append('folder_id', folderId);
      }

      const response: any = await this.request(API_ENDPOINTS.CLOUD_FILES_UPLOAD, {
        method: 'POST',
        body: formData
      });
      return response;
    } catch (error) {
      console.error('Erreur upload fichier cloud:', error);
      throw error;
    }
  }

  // Obtenir les tâches de synchronisation
  async getSyncTasks(): Promise<SyncTask[]> {
    try {
      const response: any = await this.request(API_ENDPOINTS.CLOUD_SYNC_TASKS);
      return response.results || response || [];
    } catch (error) {
      console.error('Erreur récupération tâches sync:', error);
      throw error;
    }
  }

  // Obtenir le statut d'une tâche
  async getSyncTaskStatus(taskId: number): Promise<SyncTask> {
    try {
      const response: any = await this.request(`${API_ENDPOINTS.CLOUD_SYNC_TASKS}${taskId}/status/`);
      return response;
    } catch (error) {
      console.error('Erreur récupération statut tâche:', error);
      throw error;
    }
  }

  // Obtenir les logs de synchronisation
  async getSyncLogs(connectionId?: number): Promise<SyncLog[]> {
    try {
      if (connectionId !== undefined) {
        const response: any = await this.request(API_ENDPOINTS.CLOUD_CONNECTION_LOGS(connectionId.toString()));
        return response.results || response || [];
      } else {
        // BEST PRACTICE: Utiliser buildApiUrl() pour construire l'URL de manière sûre
        const response: any = await this.request('/cloud/sync-logs/');
        return response.results || response || [];
      }
    } catch (error) {
      console.error('Erreur récupération logs sync:', error);
      throw error;
    }
  }

  // Obtenir les statistiques cloud
  async getCloudStats(): Promise<any> {
    try {
      const response: any = await this.request(API_ENDPOINTS.CLOUD_STATS);
      return response;
    } catch (error) {
      console.error('Erreur récupération stats cloud:', error);
      throw error;
    }
  }

  // Obtenir les dossiers Google Drive
  async getCloudFolders(connectionId: number, maxDepth: number = 3): Promise<{ folders: any[]; count: number }> {
    try {
      console.log(`📡 Appel API getCloudFolders pour la connexion ${connectionId}, maxDepth: ${maxDepth}`);
      const endpoint = `${API_ENDPOINTS.CLOUD_CONNECTIONS}${connectionId}/folders/?max_depth=${maxDepth}`;
      console.log(`📡 Endpoint: ${endpoint}`);
      
      // Pas de timeout pour cette requête - elle peut prendre le temps nécessaire
      // Certains utilisateurs peuvent avoir beaucoup de dossiers dans leur Google Drive
      // BEST PRACTICE: Utiliser buildApiUrl() pour construire l'URL de manière sûre
      const url = buildApiUrl(endpoint);
      const headers = this.getHeaders();
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers
        // Pas de signal/AbortController pour permettre un chargement sans limite de temps
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let errorMessage = `HTTP error! status: ${response.status}`;
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('📡 Réponse brute de l\'API getCloudFolders:', data);
      console.log('📡 Type de réponse:', typeof data);
      console.log('📡 Est un tableau?', Array.isArray(data));
      
      if (data && typeof data === 'object') {
        console.log('📡 Clés de l\'objet:', Object.keys(data));
        if (data.folders) {
          console.log(`📡 Nombre de dossiers dans response.folders: ${Array.isArray(data.folders) ? data.folders.length : 'N/A'}`);
        }
        if (data.count !== undefined) {
          console.log(`📡 Count: ${data.count}`);
        }
      }
      
      return data;
    } catch (error: any) {
      console.error('❌ Erreur récupération dossiers cloud:', error);
      console.error('❌ Détails:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status
      });
      
      throw error;
    }
  }

  // Mettre à jour les dossiers à synchroniser
  async updateSyncFolders(connectionId: number, folderIds: string[]): Promise<{ success: boolean; sync_folders: string[]; message: string }> {
    try {
      const response: any = await this.request(`${API_ENDPOINTS.CLOUD_CONNECTIONS}${connectionId}/update_sync_folders/`, {
        method: 'POST',
        body: JSON.stringify({ folder_ids: folderIds })
      });
      return response;
    } catch (error) {
      console.error('Erreur mise à jour dossiers sync:', error);
      throw error;
    }
  }
}

// Instance singleton
export const cloudService = new CloudService();